/**
 * Multi-tenant routing utilities.
 *
 * Two hosting modes:
 * 1. Path mode:     (pre.)jobs.orbio.work/<clientSlug>/<posSlug>/<uuid>
 * 2. Custom domain: custom-domain.com/<posSlug>/<uuid>  (client.custom_apply_domain)
 *
 * There are no client subdomains — *.jobs.orbio.work are environment prefixes only.
 * Configure the base domain via VITE_SUBDOMAIN_BASE_DOMAIN (default: "jobs.orbio.work").
 */

const BASE_DOMAIN = import.meta.env.VITE_SUBDOMAIN_BASE_DOMAIN || 'jobs.orbio.work';

/**
 * Repair a legacy malformed career-site identifier before it can be used for
 * region probing or API requests. Some published links ended the client/apply
 * site segment with a literal quote. Keep the repair at the browser route—the
 * source of every subsequent request—instead of teaching API lookups to accept
 * invalid identifiers.
 */
export function normalizeLegacyQuotedSlugPath(pathname: string): string {
  const parts = pathname.split('/');
  const firstSegment = parts.findIndex(Boolean);
  if (firstSegment === -1) return pathname;

  const identifierIndex = ['sites', 'internal', 'referral'].includes(parts[firstSegment])
    ? firstSegment + 1
    : firstSegment;

  if (!parts[identifierIndex]) return pathname;

  parts[identifierIndex] = parts[identifierIndex].replace(/(?:(?:%22)|")+$/gi, '');
  return parts.join('/');
}

/**
 * True when the hostname is a custom domain (not the base domain,
 * not an environment prefix of it, and not localhost).
 * On a custom domain the URL omits the client-slug path segment.
 */
export function isCustomDomain(): boolean {
  const host = window.location.hostname;
  return (
    host !== BASE_DOMAIN &&
    !host.endsWith(`.${BASE_DOMAIN}`) &&
    host !== 'localhost' &&
    host !== '127.0.0.1'
  );
}

/**
 * Validate that the current hostname is allowed to serve this client's content.
 * Returns true when the hostname matches either:
 *   - the base domain or an environment prefix (jobs.orbio.work / pre.jobs.orbio.work)
 *   - the client's custom_apply_domain
 *   - the domain in the position's apply_url (fallback when custom_apply_domain is empty)
 *   - localhost (development)
 */
export function validateHostname(customApplyDomain?: string | null, applyUrl?: string | null): boolean {
  const host = window.location.hostname;

  if (host === 'localhost' || host === '127.0.0.1') {
    return true;
  }

  if (host === BASE_DOMAIN || host.endsWith(`.${BASE_DOMAIN}`)) {
    return true;
  }

  if (customApplyDomain && host === customApplyDomain) {
    return true;
  }

  // Fallback: if the apply_url domain matches the current host, the domain is valid
  if (applyUrl) {
    try {
      const applyUrlHost = new URL(applyUrl).hostname;
      if (host === applyUrlHost) return true;
    } catch { /* ignore invalid URLs */ }
  }

  return false;
}

/**
 * Extract the client slug from the first segment of an apply_url.
 * apply_url format: "/{clientSlug}/{posSlug}/{posUuid}" or full URL.
 */
export function extractSlugFromApplyUrl(applyUrl: string): string | null {
  const path = toPathname(applyUrl);
  const parts = path.split('/').filter(Boolean);
  return parts.length >= 1 ? parts[0] : null;
}

/**
 * Extract just the pathname from an apply_url (which may be a full URL or a relative path).
 */
function toPathname(applyUrl: string): string {
  try {
    return new URL(applyUrl).pathname;
  } catch {
    return applyUrl;
  }
}

/**
 * Strip the leading client-slug segment from an apply_url when on a custom domain.
 * "/{clientSlug}/{posSlug}/{posUuid}" → "/{posSlug}/{posUuid}"
 */
export function adaptApplyUrl(applyUrl: string): string {
  const path = toPathname(applyUrl);
  if (!isCustomDomain()) return path;
  const parts = path.split('/').filter(Boolean);
  if (parts.length >= 3) {
    return '/' + parts.slice(1).join('/');
  }
  return path;
}

/**
 * Build the canonical URL for a job position based on the backend's apply_url
 * and the current access mode (custom domain or path mode).
 *
 * The backend apply_url may be in either format:
 *   - Custom domain: https://custom-domain.com/{posSlug}/{uuid}  (2 path segments)
 *   - Default:       https://jobs.orbio.work/{clientSlug}/{posSlug}/{uuid}  (3 path segments)
 *
 * Priority:
 * 1. Custom domain (if available) → build URL on the custom domain
 * 2. Path mode → https://{baseDomain}/{clientSlug}/{posSlug}/{uuid}
 */
export function getCanonicalJobUrl(applyUrl: string, customApplyDomain?: string): string {
  try {
    const url = new URL(applyUrl);
    const pathParts = url.pathname.split('/').filter(Boolean);

    if (customApplyDomain) {
      if (pathParts.length >= 3) {
        return `https://${customApplyDomain}/${pathParts[1]}/${pathParts[2]}`;
      }
      if (pathParts.length >= 2) {
        return `https://${customApplyDomain}/${pathParts[0]}/${pathParts[1]}`;
      }
      return applyUrl;
    }

    if (pathParts.length < 3) return applyUrl;

    return `https://${BASE_DOMAIN}/${pathParts.join('/')}`;
  } catch {
    return applyUrl;
  }
}
